/**
 * Utility to generate Code 128 (Set B) SVG barcode strings and Data URIs.
 * Designed for thermal label printing (4x6 format) with clean scalable vectors.
 */

// Code 128 patterns for values 0..106
const CODE128_PATTERNS = [
    [2,1,2,2,2,1], [2,2,2,1,2,1], [2,2,2,2,2,1], [1,2,1,2,2,3], [1,2,1,3,2,2],
    [1,3,1,2,2,2], [1,2,2,2,1,3], [1,2,2,3,1,2], [1,3,2,2,1,2], [2,2,1,2,1,3],
    [2,2,1,3,1,2], [2,3,1,2,1,2], [1,1,2,2,3,2], [1,2,2,1,3,2], [1,2,2,2,3,1],
    [1,1,3,2,2,2], [1,2,3,1,2,2], [1,2,3,2,2,1], [2,2,3,2,1,1], [2,2,1,1,3,2],
    [2,2,1,2,3,1], [2,1,3,2,1,2], [2,2,3,1,1,2], [3,1,2,1,3,1], [3,1,1,2,2,2],
    [3,2,1,1,2,2], [3,2,1,2,2,1], [3,1,2,2,1,2], [3,2,2,1,1,2], [3,2,2,2,1,1],
    [2,1,2,1,2,3], [2,1,2,3,2,1], [2,3,2,1,2,1], [1,1,1,3,2,3], [1,3,1,1,2,3],
    [1,3,1,3,2,1], [1,1,2,3,1,3], [1,3,2,1,1,3], [1,3,2,3,1,1], [2,1,1,3,1,3],
    [2,3,1,1,1,3], [2,3,1,3,1,1], [1,1,2,1,3,3], [1,1,2,3,3,1], [1,3,2,1,3,1],
    [1,1,3,1,2,3], [1,1,3,3,2,1], [1,3,3,1,2,1], [3,1,3,1,2,1], [2,1,1,3,3,1],
    [2,3,1,1,3,1], [2,1,3,1,1,3], [2,1,3,3,1,1], [2,1,3,1,3,1], [3,1,1,1,2,3],
    [3,1,1,3,2,1], [3,3,1,1,2,1], [3,1,2,1,1,3], [3,1,2,3,1,1], [3,3,2,1,1,1],
    [3,1,4,1,1,1], [2,2,1,4,1,1], [4,3,1,1,1,1], [1,1,1,2,2,4], [1,1,1,4,2,2],
    [1,2,1,1,2,4], [1,2,1,4,2,1], [1,4,1,1,2,2], [1,4,1,2,2,1], [1,1,2,2,1,4],
    [1,1,2,4,1,2], [1,2,2,1,1,4], [1,2,2,4,1,1], [1,4,2,1,1,2], [1,4,2,2,1,1],
    [2,4,1,2,1,1], [2,2,1,1,1,4], [4,1,3,1,1,1], [2,4,1,1,1,2], [1,3,4,1,1,1],
    [1,1,1,2,4,2], [1,2,1,1,4,2], [1,2,1,2,4,1], [1,1,4,2,1,2], [1,2,4,1,1,2],
    [1,2,4,2,1,1], [4,1,1,2,1,2], [4,2,1,1,1,2], [4,2,1,2,1,1], [2,1,2,1,4,1],
    [2,1,4,1,2,1], [4,1,2,1,2,1], [1,1,1,1,4,3], [1,1,1,3,4,1], [1,3,1,1,4,1],
    [1,1,4,1,1,3], [1,1,4,3,1,1], [4,1,1,1,1,3], [4,1,1,3,1,1], [1,1,3,1,4,1],
    [1,1,4,1,3,1], [3,1,1,1,4,1], [4,1,1,1,3,1], [2,1,1,4,1,2], [2,1,1,2,1,4],
    [2,1,1,2,3,2], [2,3,3,1,1,1,2] // Stop pattern (106)
];

export const generateCode128Svg = (text, height = 70, moduleWidth = 2) => {
    if (!text || typeof text !== 'string') return '';

    // Encode Code 128 Set B
    const codes = [104]; // Start Code B
    let checksum = 104;

    for (let i = 0; i < text.length; i++) {
        let code = text.charCodeAt(i) - 32;
        if (code < 0 || code > 95) code = 0; // fallback to space
        codes.push(code);
        checksum += (i + 1) * code;
    }

    checksum = checksum % 103;
    codes.push(checksum);
    codes.push(106); // Stop pattern

    // Build modules sequence (bars & spaces)
    let totalModules = 0;
    const modules = [];

    codes.forEach(codeIdx => {
        const pattern = CODE128_PATTERNS[codeIdx];
        if (!pattern) return;
        pattern.forEach((width, pIdx) => {
            const isBar = pIdx % 2 === 0;
            modules.push({ isBar, width });
            totalModules += width;
        });
    });

    // Add quiet zone (10 modules left and right)
    const quietZone = 10;
    const svgWidth = (totalModules + quietZone * 2) * moduleWidth;

    let currentX = quietZone * moduleWidth;
    let rects = '';

    modules.forEach(({ isBar, width }) => {
        const barWidth = width * moduleWidth;
        if (isBar) {
            rects += `<rect x="${currentX}" y="0" width="${barWidth}" height="${height}" fill="#000000" />`;
        }
        currentX += barWidth;
    });

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgWidth} ${height}" width="100%" height="auto" style="display:block;margin:0 auto;max-width:100%;height:auto;background:#ffffff;">${rects}</svg>`;
};

export const generateCode128DataUrl = (text, height = 70, moduleWidth = 2) => {
    const svg = generateCode128Svg(text, height, moduleWidth);
    if (!svg) return '';
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};
